/**
 * The route-log cache — the pure part (docs/spec/11-map-routes.md, "Loading
 * the state"; docs/spec/01f-tab-loading.md, "The always-run prefetch").
 *
 * The route modal needs the climber's own entries of a route from EVERY day
 * before its buttons may act (the Flash rule, the project's tries). Instead of
 * one query per open, the background prefetch reads the climber's day
 * documents once and groups them by route; these functions are that grouping,
 * the read window, the coverage rule and the fold of the live day document.
 * The stateful wrapper is `hooks/useRouteLogCache.js`.
 */
import { climbAtMillis } from "@sendly/shared";
import { flattenClimbLogs } from "./climbLog";

/** The window never reaches further back than this — older routes fall back to the per-route query. */
export const ROUTE_LOG_CACHE_MAX_DAYS = 180;

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** A "YYYY-MM-DD" day key — the unit of `route.createdAt` and of a day document's `date`. */
export function isDayKey(v) {
  return typeof v === "string" && DAY_KEY.test(v);
}

/** `dayKey` moved by `days` calendar days. UTC arithmetic: a day key has no zone. */
export function shiftDayKey(dayKey, days) {
  const [y, m, d] = dayKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * The first day the prefetch reads: the oldest `createdAt` among the active
 * routes — no entry of a route can sit on a day before it was created — but at
 * most `maxDays` before `today`. `null` when there is nothing to read (no
 * active route with a valid `createdAt`, or no valid `today`).
 */
export function routeLogWindowStart(routes, today, maxDays = ROUTE_LOG_CACHE_MAX_DAYS) {
  if (!isDayKey(today)) return null;
  let oldest = null;
  for (const r of routes || []) {
    if (!r || r.active === false || !isDayKey(r.createdAt)) continue;
    if (oldest === null || r.createdAt < oldest) oldest = r.createdAt;
  }
  if (oldest === null) return null;
  const floor = shiftDayKey(today, -maxDays);
  return oldest < floor ? floor : oldest;
}

/**
 * Day documents → `Map(routeId → entries)`, each list newest first and each
 * entry carrying its `cid`, `date` and `userId` — the shape `fetchRouteClimbs`
 * hands the route modal. Entries without a `routeId` (the spray half of a day)
 * are left out.
 */
export function groupClimbLogsByRoute(dayDocs) {
  const byRoute = new Map();
  for (const entry of flattenClimbLogs(dayDocs)) {
    const rid = entry.routeId;
    if (!rid) continue;
    const list = byRoute.get(rid);
    if (list) list.push(entry);
    else byRoute.set(rid, [entry]);
  }
  return byRoute;
}

/**
 * Does a cache read from `from` hold every entry of `route`? Only when the
 * route was created on or after `from`; a route without a valid `createdAt`
 * is never covered.
 */
export function routeLogCacheCovers(from, route) {
  return isDayKey(from) && isDayKey(route?.createdAt) && route.createdAt >= from;
}

/**
 * Replace one day's entries in `byRoute` with that day's document — the live
 * document of today, so the day's last state survives midnight. Returns a new
 * Map; lists of routes the day does not touch are kept as they are. A route
 * whose list ends up empty is dropped from the map (an empty list and a
 * missing one read the same).
 */
export function foldDayIntoRouteLog(byRoute, dayDoc) {
  const date = dayDoc?.date;
  if (!date) return byRoute;
  const dayByRoute = groupClimbLogsByRoute([dayDoc]);
  const next = new Map();
  for (const [rid, list] of byRoute) {
    if (!list.some(e => e.date === date) && !dayByRoute.has(rid)) { next.set(rid, list); continue; }
    const merged = [...list.filter(e => e.date !== date), ...(dayByRoute.get(rid) || [])];
    if (merged.length) next.set(rid, sortNewestFirst(merged));
  }
  for (const [rid, list] of dayByRoute) {
    if (!byRoute.has(rid)) next.set(rid, list);
  }
  return next;
}

// The order rule of `flattenClimbLogs`, for a list merged from two sources.
function sortNewestFirst(list) {
  return list.sort((a, b) => climbAtMillis(b) - climbAtMillis(a));
}
